import type { Printer, PrintLog, PrintElement } from '@features/printer/types';

// @ts-ignore
import qz from 'qz-tray';

export const findPrinterByType = (printers: Printer[], type: string): Printer | undefined =>
  printers.find((printer: Printer) => printer.type === type);

/** Receives the outcome of every job so the UI (print history, printer panel) can show it. Set once by the PrinterProvider. */
export type PrintJobReporter = {
  create: (log: PrintLog) => void;
  update: (id: string, patch: Partial<PrintLog>) => void;
};

let reporter: PrintJobReporter | null = null;

export const setPrintJobReporter = (next: PrintJobReporter | null): void => {
  reporter = next;
};

export const createPrintLogId = (): string => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/**
 * Describes the job for the history. With an `id`, the log already exists (created by the caller) and is updated;
 * without one, a log entry is created from `label` / `reference`.
 */
export type PrintJobMeta = { id?: string; label?: string; reference?: string };

const errorMessage = (error: unknown): string => (error instanceof Error ? error.message : String(error));

/**
 * Sends a job to QZ Tray without blocking the caller (`send` runs synchronously) and records how it went.
 * The duration is the time QZ Tray took to accept the job, not the physical print time: printers do not report it.
 */
const track = (printerName: string, send: () => unknown, meta: PrintJobMeta = {}): Promise<void> => {
  const id = meta.id ?? createPrintLogId();
  if (!meta.id) {
    reporter?.create({
      id,
      date: new Date().toISOString(),
      name: meta.reference ?? printerName,
      type: meta.label ?? 'Impression',
      link: '',
      printer: printerName,
      status: 'pending',
    });
  }

  const startedAt = performance.now();
  const elapsed = () => Math.round(performance.now() - startedAt);

  let result: unknown;
  try {
    result = send();
  } catch (error) {
    result = Promise.reject(error);
  }

  return Promise.resolve(result).then(
    () => {
      reporter?.update(id, { status: 'sent', durationMs: elapsed() });
    },
    (error: unknown) => {
      reporter?.update(id, { status: 'failed', durationMs: elapsed(), error: errorMessage(error) });
    }
  );
};

export class PrintHandler {
  static print(printers: Printer[], elements: PrintElement[], orderReference: string): PrintLog[] {
    const logs: PrintLog[] = [];
    elements.forEach((element: PrintElement) => {
      const printer = findPrinterByType(printers, element.printer);
      const log: PrintLog = {
        id: createPrintLogId(),
        date: new Date().toISOString(),
        name: orderReference,
        type: element.title,
        link: element.url ?? '',
        printer: printer?.originalName,
        status: printer ? 'pending' : 'skipped',
      } as PrintLog;
      logs.push(log);
      if (!printer) {
        return;
      }
      switch (element.format) {
        case 'pdf':
          PrintHandler.pdf(printer.originalName, element.url!, element.copies, element.bothSides, { id: log.id });
          break;
        case 'zpl': {
          const data = (element.base64 ? atob(element.data) : element.data).split('\n').map((line) => line + '\n');
          PrintHandler.zpl(printer.originalName, data, element.copies, { id: log.id });
          break;
        }
        default:
          break;
      }
    });

    return logs;
  }

  static pdf(
    printer: string,
    assetUrl: string,
    copies: number = 1,
    bothSides: boolean = false,
    meta?: PrintJobMeta
  ): Promise<void> {
    const options: { copies: number; duplex?: any } = { copies };
    if (bothSides) {
      options.duplex = 'short-edge';
    }
    const config = qz.configs.create(printer, options);
    return track(
      printer,
      () =>
        qz.print(config, [
          {
            type: 'pixel',
            format: 'pdf',
            flavor: 'file',
            data: assetUrl,
          },
        ]),
      meta
    );
  }

  static zpl(printer: string, zpl: string[], copies: number = 1, meta?: PrintJobMeta): Promise<void> {
    // @ts-ignore
    const config = qz.configs.create(printer, { copies, language: 'ZPL', altPrinting: true });
    return track(printer, () => qz.print(config, zpl), meta);
  }
}
